import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import AdminLayout from "@/components/admin/AdminLayout";
import axios from "axios";
import { 
  Users, 
  FileText, 
  FolderOpen, 
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  Clock
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const [statsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/admin/leads`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStats(statsRes.data);
      setRecentLeads(leadsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Dashboard error:", error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("admin_token");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-slate-400">Overview of SolveDebts performance</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-400" />
              </div>
              <div className="flex items-center gap-1 text-emerald-400 text-sm">
                <ArrowUpRight className="w-4 h-4" />
                <span>+{stats?.today?.leads || 0} today</span>
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{stats?.totals?.leads || 0}</p>
            <p className="text-slate-400 text-sm">Total Leads</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <FolderOpen className="w-6 h-6 text-purple-400" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{stats?.totals?.cases || 0}</p>
            <p className="text-slate-400 text-sm">Active Cases</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{stats?.totals?.conversion_rate || 0}%</p>
            <p className="text-slate-400 text-sm">Conversion Rate</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-400" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-1">{formatCurrency(stats?.average_debt)}</p>
            <p className="text-slate-400 text-sm">Avg. Debt Amount</p>
          </motion.div>
        </div>

        {/* Charts and Tables Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Lead Trend Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <h2 className="text-lg font-semibold text-white mb-6">Lead Trend (Last 7 Days)</h2>
            <div className="h-48 flex items-end justify-between gap-2">
              {stats?.daily_leads?.map((day, i) => {
                const maxCount = Math.max(...(stats?.daily_leads?.map(d => d.count) || [1]));
                const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-2">
                    <div 
                      className="w-full bg-[#10B981] rounded-t-lg transition-all duration-500"
                      style={{ height: `${Math.max(height, 5)}%` }}
                    ></div>
                    <span className="text-xs text-slate-500">
                      {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'short' })}
                    </span>
                    <span className="text-xs text-slate-400 font-medium">{day.count}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Case Status Breakdown */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-slate-800 rounded-2xl p-6 border border-slate-700"
          >
            <h2 className="text-lg font-semibold text-white mb-6">Case Status Breakdown</h2>
            <div className="space-y-4">
              {Object.entries(stats?.case_statuses || {}).map(([status, count]) => {
                const total = Object.values(stats?.case_statuses || {}).reduce((a, b) => a + b, 0);
                const percent = total > 0 ? (count / total) * 100 : 0;
                const colors = {
                  documents_required: "bg-amber-500",
                  under_review: "bg-purple-500",
                  proposal_drafting: "bg-indigo-500",
                  creditor_voting: "bg-cyan-500",
                  active: "bg-emerald-500",
                  completed: "bg-green-600"
                };
                return (
                  <div key={status}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-300 capitalize">{status.replace(/_/g, ' ')}</span>
                      <span className="text-slate-400">{count}</span>
                    </div>
                    <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${colors[status] || 'bg-slate-500'} rounded-full transition-all duration-500`}
                        style={{ width: `${percent}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Recent Leads */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden"
        >
          <div className="p-6 border-b border-slate-700 flex justify-between items-center">
            <h2 className="text-lg font-semibold text-white">Recent Leads</h2>
            <button 
              onClick={() => navigate("/admin/leads")}
              className="text-[#10B981] text-sm hover:underline"
            >
              View all
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-700/50">
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-3">Name</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-3">Debt</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-slate-400 uppercase px-6 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700">
                {recentLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-slate-700/30">
                    <td className="px-6 py-4">
                      <div>
                        <p className="text-white font-medium">{lead.full_name}</p>
                        <p className="text-slate-400 text-sm">{lead.email}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-white font-medium">{formatCurrency(lead.debt_amount)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        lead.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                        lead.status === 'contacted' ? 'bg-amber-500/20 text-amber-400' :
                        lead.status === 'converted' ? 'bg-emerald-500/20 text-emerald-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {lead.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-400 text-sm">
                      {formatDate(lead.created_at)}
                    </td>
                  </tr>
                ))}
                {recentLeads.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-slate-400">
                      No leads yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
